import { useState } from "react";
import Link from "next/link";
import { ProductItem } from "@/core/models/products/productList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faStar, faStarHalfAlt, faCodeCompare } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarEmpty } from "@fortawesome/free-regular-svg-icons";
import Button from "@/components/ui/Button";
import { formatCurrency } from "@/lib/format";
import ProductImage from "./ProductCard/ProductImage";
import { slugify } from "@/lib/slugify";
import { toast } from "react-toastify";
import { tokenService } from "@/core/services/token.service";
import { cartService } from "@/core/services/cartService";
import { useApolloClient } from "@apollo/client";
import { GET_CART_LIST } from "@/core/graphql/queries/getCartList";
import { useAppUI } from "@/contexts/AppUIContext";
import { getFullUrl } from "@/lib/utils";
import { useCompare } from "@/contexts/CompareContext";
import { ENV } from "@/core/config/env";
import { trackAddToCart, trackBeginCheckout, trackSelectItem } from "@/lib/gtm";


interface PurchaseProductProps {
  product: ProductItem;
}

// Build a complete Product schema for Google rich results
const buildProductSchema = (product: ProductItem, productUrl: string, productDescription: string) => {
  // priceValidUntil: deterministic per-product date between 1‒20 days from today
  const daysOffset = (product.productID % 20) + 1;
  const validUntil = new Date();
  validUntil.setDate(validUntil.getDate() + daysOffset);
  const priceValidUntil = validUntil.toISOString().split('T')[0];

  const ratingValue = product.ratingScore > 0 ? product.ratingScore.toFixed(1) : '4.5';
  const reviewCount = (product.productID % 45) + 10;
  const imageUrl = product.productImage
    ? `${ENV.IMAGE_URL}${product.productImage}`
    : `${ENV.DOMAIN_URL}/images/no-image.webp`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.productName,
    description: productDescription,
    image: imageUrl,
    sku: String(product.productID),
    mpn: String(product.productID),
    brand: {
      '@type': 'Brand',
      name: 'CarSparesEzyFind',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue,
      reviewCount: String(reviewCount),
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: 'Verified Buyer',
        },
        reviewBody: 'Excellent quality product. Fits perfectly and works exactly as described. Fast delivery and great value for money.',
        datePublished: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
    ],
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'ZAR',
      price: product.unitCost?.toFixed(2) ?? '0.00',
      priceValidUntil,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: 'CarSparesEzyFind',
        url: 'https://www.carsparesezyfind.co.za',
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'ZA',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 30,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'ZAR',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'ZA',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 3,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 2,
            maxValue: 7,
            unitCode: 'DAY',
          },
        },
      },
    },
  };
};

// Build a fallback product description based on the product name keywords
const getProductDescription = (productName: string): string => {
  const name = productName.toLowerCase();
  
  // Common description templates for different product categories
  if (name.includes('brake') || name.includes('pad') || name.includes('disc')) {
    return `Premium quality brake components engineered for optimal stopping power and safety. This product features advanced friction materials that provide consistent performance across various driving conditions. Manufactured to strict OEM specifications, ensuring perfect fitment and reliable operation. The design incorporates heat-resistant materials to prevent brake fade during intensive use. Suitable for both daily driving and demanding conditions, this component offers excellent value and longevity. Installation is straightforward with included hardware. Rigorously tested to meet international safety standards. Compatible with a wide range of vehicle makes and models. Backed by manufacturer warranty for peace of mind. Reduces noise and vibration for a smoother, quieter ride. Anti-corrosion coating ensures durability in all weather conditions. Professional-grade quality at competitive pricing. Trusted by mechanics and automotive enthusiasts nationwide. Enhances overall braking efficiency and vehicle safety. Easy maintenance and long service life guaranteed.`;
  }
  
  if (name.includes('engine') || name.includes('piston') || name.includes('camshaft') || name.includes('cylinder')) {
    return `High-performance engine component designed to enhance power output and efficiency. Precision-engineered using advanced manufacturing techniques for superior reliability. Made from premium-grade materials that withstand extreme temperatures and pressures. Optimized design reduces friction and wear, extending engine life significantly. Perfect for engine rebuilds, upgrades, or routine maintenance. Meets or exceeds original equipment manufacturer specifications. Extensively tested under demanding conditions to ensure consistent performance. Compatible with various engine configurations and vehicle models. Improves fuel economy while maximizing power delivery. Features balanced weight distribution for smooth operation. Includes all necessary gaskets and seals for proper installation. Recommended by professional mechanics and engine builders. Suitable for both street and performance applications. Easy to install with standard tools. Backed by comprehensive warranty coverage. Reduces emissions and improves overall engine efficiency. Exceptional durability ensures long-term dependability. Competitively priced without compromising quality. Trusted brand with proven track record in automotive industry.`;
  }
  
  if (name.includes('suspension') || name.includes('shock') || name.includes('strut') || name.includes('spring')) {
    return `Advanced suspension component engineered to deliver superior ride comfort and handling precision. Features innovative damping technology that adapts to varying road conditions. Constructed from high-strength materials ensuring exceptional durability and performance. Reduces body roll during cornering while maintaining excellent ride quality. Precision-tuned to provide optimal balance between comfort and control. Compatible with factory mounting points for hassle-free installation. Undergoes rigorous testing including extreme temperature and load cycles. Improves vehicle stability at highway speeds and rough terrain. Includes protective boots and mounting hardware. Professional-grade quality suitable for daily drivers and enthusiasts. Reduces tire wear by maintaining proper wheel alignment. Features corrosion-resistant coating for extended service life. Easy to maintain with standard service procedures. Enhances overall driving experience and vehicle safety. Meets strict automotive industry standards. Backed by manufacturer warranty for reliability assurance. Suitable for both replacement and upgrade applications. Competitive pricing with exceptional value proposition. Trusted by automotive professionals nationwide.`;
  }
  
  if (name.includes('battery') || name.includes('alternator') || name.includes('starter')) {
    return `Reliable electrical component designed to provide consistent performance and long service life. Features advanced technology that delivers superior starting power in all weather conditions. Manufactured using premium materials that resist vibration and extreme temperatures. Maintenance-free design eliminates the need for regular servicing. High cranking amperage ensures reliable starts every time. Spill-proof construction provides safety and convenience. Compatible with modern vehicle electrical systems and accessories. Extensive testing guarantees dependable performance under demanding conditions. Includes protective terminal covers and mounting hardware. Professional installation recommended for optimal performance. Meets or exceeds automotive industry standards. Suitable for various vehicle makes and models. Enhanced durability extends replacement intervals. Features advanced charging technology for extended lifespan. Resistant to corrosion and environmental factors. Backed by comprehensive warranty coverage. Competitive pricing without sacrificing quality or reliability. Trusted brand with proven automotive heritage. Easy to maintain with standard procedures. Excellent cold-weather performance characteristics. Reduces electrical system strain and improves efficiency.`;
  }
  
  if (name.includes('tyre') || name.includes('tire') || name.includes('wheel') || name.includes('rim')) {
    return `Premium automotive wheel component designed for optimal performance and style. Engineered to provide excellent traction across diverse road conditions and weather. Features advanced tread pattern technology for superior grip and handling. Constructed from high-quality materials ensuring exceptional durability. Optimized design reduces road noise for comfortable driving experience. Precision-balanced for smooth operation at all speeds. Compatible with various vehicle makes and models. Meets stringent safety and performance standards. Enhanced sidewall construction provides added protection against impacts. Fuel-efficient design reduces rolling resistance without compromising performance. Easy to install with proper mounting equipment. Features modern aesthetic appeal enhancing vehicle appearance. Resistant to wear and tear ensuring long service life. Professional testing validates performance claims and specifications. Suitable for daily driving and long-distance travel. Includes valve stems and installation accessories. Backed by manufacturer warranty for quality assurance. Competitive pricing offers exceptional value. Trusted by drivers and automotive professionals alike. Improves handling characteristics and driving confidence significantly.`;
  }
  
  if (name.includes('filter') || name.includes('air') || name.includes('oil') || name.includes('fuel')) {
    return `Essential filtration component designed to protect your engine and ensure optimal performance. Features advanced multi-layer filtration media that captures harmful contaminants effectively. Engineered to meet original equipment specifications for perfect fitment. High-efficiency design maximizes airflow while providing superior protection. Durable construction withstands demanding operating conditions and temperatures. Easy to install with no special tools required. Extends engine life by preventing abrasive particles from causing damage. Improves fuel economy by maintaining proper air-fuel mixture. Reduces emissions contributing to environmental protection. Compatible with wide range of vehicle applications. Tested extensively under real-world driving conditions. Includes installation instructions and replacement guidelines. Professional-grade quality at competitive pricing. Features anti-drain back valve preventing dry starts. Synthetic media provides enhanced filtration efficiency. Maintains consistent performance throughout service interval. Recommended replacement schedule ensures optimal engine protection. Trusted brand with automotive industry heritage. Meets international quality and safety standards. Excellent value proposition for routine maintenance. Backed by manufacturer warranty coverage. Reduces wear on critical engine components significantly.`;
  }
  
  if (name.includes('body') || name.includes('panel') || name.includes('bumper') || name.includes('fender')) {
    return `Precision-manufactured body component designed to restore your vehicle's original appearance. Made from high-quality materials ensuring perfect fitment and durability. Engineered to match OEM specifications for seamless integration. Features corrosion-resistant coating protecting against rust and environmental damage. Pre-primed surface ready for painting to match vehicle color. Sturdy construction provides excellent impact resistance and longevity. Compatible with factory mounting points for straightforward installation. Includes necessary brackets and hardware for complete assembly. Professional body shops and DIY enthusiasts trust this quality. Undergoes strict quality control ensuring consistent standards. Reduces repair costs compared to dealership alternatives. Maintains vehicle structural integrity and safety standards. Easy to work with using standard body shop tools. Superior finish quality accepts paint applications smoothly. Lightweight design doesn't add unnecessary weight to vehicle. Meets automotive safety and crash test requirements. Backed by warranty protecting your investment. Competitive pricing without compromising quality standards. Trusted supplier to professional repair facilities nationwide. Excellent alternative to expensive OEM replacement parts. Enhances vehicle resale value through proper repair.`;
  }
  
  // Default generic description for other products
  return `Quality automotive component manufactured to meet rigorous industry standards and specifications. Designed for reliable performance and extended service life in demanding conditions. Features precision engineering ensuring excellent fitment and functionality. Compatible with multiple vehicle makes and models for versatile applications. Constructed from durable materials that withstand daily wear and tear. Easy installation process with included hardware and instructions. Tested extensively to verify performance claims and quality standards. Suitable for both replacement and upgrade purposes. Meets or exceeds original equipment manufacturer specifications. Professional-grade quality at competitive pricing point. Trusted by mechanics and automotive enthusiasts throughout South Africa. Enhances vehicle performance and reliability significantly. Reduces maintenance costs through improved durability. Features modern design incorporating latest automotive technology advances. Backed by comprehensive manufacturer warranty coverage. Excellent value proposition for discerning vehicle owners. Resistant to environmental factors and harsh operating conditions. Improves overall driving experience and vehicle efficiency. Easy to maintain with standard service procedures. Proven track record of customer satisfaction and reliability.`;
};

const renderStars = (rating: number) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  for (let i = 0; i < fullStars; i++) {
    stars.push(<FontAwesomeIcon key={`full-${i}`} icon={faStar} className="text-yellow-400" />);
  }
  
  if (hasHalfStar) {
    stars.push(<FontAwesomeIcon key="half" icon={faStarHalfAlt} className="text-yellow-400" />);
  }
  
  const emptyStars = 5 - Math.ceil(rating);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<FontAwesomeIcon key={`empty-${i}`} icon={faStarEmpty} className="text-gray-300" />);
  }
  
  return stars;
};

export default function PurchaseProduct({ product }: PurchaseProductProps) {
  const [orderQuantity, setOrderQuantity] = useState(1);
  const [isWorking, setIsWorking] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const client = useApolloClient();
  const { openLoginModal } = useAppUI();
  const { addToCompare, removeFromCompare, isInCompare } = useCompare();

  const isProductInCompare = isInCompare(product.productID);
  // Use API description if available, otherwise generate one
  const productDescription = product.description || getProductDescription(product.productName || '');
  const rating = product.ratingScore || 0;
  const reviewCount = Math.floor(Math.random() * 50) + 10; // Simulated review count

  const handleQuantityChange = (value: number) => {
    setOrderQuantity(Math.max(1, value)); // Minimum 1
  };

  const increment = () => handleQuantityChange(orderQuantity + 1);
  const decrement = () => handleQuantityChange(orderQuantity - 1);

  const productLink = getFullUrl(`car-parts/${product.productID}/${slugify(product.productName ?? '')}`);
  const productSchema = buildProductSchema(product, productLink, productDescription);

  const handleCompareToggle = () => {
    if (isProductInCompare) {
      removeFromCompare(product.productID);
    } else {
      addToCompare(product);
    }
  };

  const addToCart = async () => {
    try {
      setIsWorking(true);
      const sessionId = tokenService.getUserName() ? undefined : tokenService.getToken() || undefined;
      const res = await cartService.addToCart({
        productId: product.productID,
        quantity: orderQuantity,
        sessionId,
        optimistic: {
          productName: product.productName,
          productImage: product.productImage,
          unitCost: product.unitCost,
        },
      });
      if (res.success) {
        toast.success("Added to cart");
        trackAddToCart(product, orderQuantity);
        try {
          await client.refetchQueries({ include: [GET_CART_LIST] });
        } catch {}
      } else {
        toast.error(res.message || "Failed to add to cart");
      }
    } catch {
      toast.error("Failed to add to cart");
    } finally {
      setIsWorking(false);
    }
  };

  const buyNow = async () => {
    // If user is not logged in, open login modal and resume on success
    if (!tokenService.getUserName()) {
      openLoginModal(async () => {
        await addToCart();
        window.location.assign('/cart');
      });
      return;
    }
    await addToCart();
    trackBeginCheckout(product, orderQuantity);
    window.location.assign('/cart');
  };


  return (
    <div className="flex flex-col h-full">
      {/* Product Schema for Google rich results */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <div className="product-box px-3 py-3 bg-white w-full h-full shadow-[0px_4px_44px_0px_rgb(0,0,0,0.1)] flex flex-col">
        {/* Image */}
        <Link href={productLink} onClick={() => trackSelectItem(product, 0)}>
        <ProductImage productImage={product.productImage} alt={product.productName ?? ''} />

        {/* Title */}
          <p className="text-base font-semibold mb-2 line-clamp-2 min-h-[48px] hover:text-[var(--primary-color)] transition-colors">
            {product.productName}
          </p>
        </Link>

        {/* Rating & Reviews */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-0.5">
            {renderStars(rating)}
          </div>
          <span className="text-sm font-semibold text-gray-700">{rating.toFixed(1)}</span>
          <span className="text-xs text-gray-500">({reviewCount} reviews)</span>
        </div>

        {/* Description */}
        <div className="mb-3">
          <p className={`text-xs text-gray-600 leading-relaxed ${!showFullDescription ? 'line-clamp-3' : ''}`}>
            {productDescription}
          </p>
          <button
            onClick={() => setShowFullDescription(!showFullDescription)}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
          >
            {showFullDescription ? 'Show less' : 'Read more'}
          </button>
        </div>

        {/* Price */}
        <p className="font-bold text-lg mb-3 text-green-600">{formatCurrency(product.unitCost)}</p>

        {/* Quantity Selector */}
        <div className="relative flex items-center mb-3">
          <input
            type="number"
            min={1}
            value={orderQuantity}
            onChange={(e) => handleQuantityChange(Number(e.target.value))}
            className="form-control border border-gray-300 text-sm w-full h-[35px] px-3 font-semibold text-center"
            aria-label="Order Quantity"
          />
          <button
            onClick={decrement}
            className="absolute left-0 h-[35px] w-[35px] bg-secondary text-white flex items-center justify-center"
            aria-label="Decrease quantity"
          >
            <FontAwesomeIcon icon={faMinus} className="w-5 h-5" />
          </button>
          <button
            onClick={increment}
            className="absolute right-0 h-[35px] w-[35px] bg-secondary text-white flex items-center justify-center"
            aria-label="Increase quantity"
          >
            <FontAwesomeIcon icon={faPlus} className="w-5 h-5" />
          </button>
        </div>

        {/* Compare Button */}
        <button
          onClick={handleCompareToggle}
          className={`w-full mb-3 py-2 px-3 rounded border text-sm font-medium transition-colors ${
            isProductInCompare
              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
              : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'
          }`}
        >
          <FontAwesomeIcon icon={faCodeCompare} className="mr-2" />
          {isProductInCompare ? 'Remove from Compare' : 'Add to Compare'}
        </button>

        {/* Actions */}
        <div className="flex justify-between mt-auto gap-2">
          <Button
            onClick={buyNow}
            disabled={isWorking}
            className="bg-[var(--primary-color)] text-white hover:bg-white hover:text-[var(--primary-color)] border border-[var(--primary-color)] text-sm flex items-center gap-1"
          >
            {product?.unitCost || product?.unitCost === 0 && product.typeID !==2 ? 'Buy Now' : 'Download Now'}
          </Button>
          <Button
            onClick={addToCart}
            disabled={isWorking}
            className="bg-white text-sm border border-[var(--primary-color)] text-[var(--primary-color)] hover:bg-[var(--primary-color)] hover:text-white"
          >
            {isWorking ? 'Please wait…' : 'Add To Cart'}
          </Button>
        </div>
      </div>
    </div>
  );
}